import { useMemo, useState } from "react";
import SectionTitle from "./SectionTitle";

type BlogCategory =
  | "Getting Started"
  | "Slots"
  | "Live Casino"
  | "Sports"
  | "Bonuses"
  | "Payments"
  | "Mobile"
  | "Safety"
  | "Strategy"
  | "Community";

type BlogPost = {
  id: string;
  category: BlogCategory;
  title: string;
  readingTime: string;
  excerpt: string;
  sections: Array<{
    heading: string;
    paragraphs: string[];
    tips?: string[];
  }>;
};

const posts: BlogPost[] = [
  {
    id: "start-here-aussie-beginners",
    category: "Getting Started",
    title: "Start here: how Aussie players usually set up Ace96au in 10 minutes",
    readingTime: "8 min",
    excerpt:
      "A quick, real-world walkthrough: creating an account, picking the first game, choosing a deposit method that won’t slow down withdrawals later, and a few common “gotchas” to avoid.",
    sections: [
      {
        heading: "A simple first session (what most people do)",
        paragraphs: [
          "If you’re brand new, don’t overthink it. Most players want three things on day one: an account that’s ready to play, a deposit route that works reliably in AUD, and a game that’s easy to understand.",
          "The “smoothest” setup is usually: confirm your profile details, choose a deposit method that matches how you want to withdraw later, then start with a simple slot or low‑stakes live table to get comfortable with the interface.",
        ],
        tips: [
          "Use the same name everywhere (profile and payment method). It saves time later.",
          "If you plan to withdraw in AUD, start on PayID/Osko where possible.",
          "Take a screenshot of your first successful deposit reference just in case you ever need tracing.",
        ],
      },
      {
        heading: "Picking your first game without feeling lost",
        paragraphs: [
          "A good first pick is a slot with clear features (free spins, wilds, multipliers). You learn the flow quickly and you’re not juggling table rules.",
          "If you prefer human‑dealer games, start with Roulette or Baccarat at low limits. You’ll feel the “live casino” vibe without needing a big bankroll.",
        ],
      },
      {
        heading: "Why your deposit choice matters more than people think",
        paragraphs: [
          "Deposits are often instant. Withdrawals can take longer because name matching, fraud checks, and method availability all matter.",
          "If you always use one consistent method, your account history stays tidy, which generally helps with smoother processing later on.",
        ],
      },
    ],
  },
  {
    id: "slots-rtp-volatility",
    category: "Slots",
    title: "Slots 101: RTP vs volatility (and what it means for your wallet)",
    readingTime: "10 min",
    excerpt:
      "RTP looks simple, but it doesn’t tell you how “swingy” a slot feels. Here’s a practical way to use RTP and volatility when you pick games.",
    sections: [
      {
        heading: "RTP is the long‑run average, not a promise",
        paragraphs: [
          "RTP (Return to Player) is a statistical average over a huge number of spins. It doesn’t guarantee short‑term outcomes. Two players can spin the same game for an hour and have totally different results.",
          "A slot with higher RTP can still feel brutal in the short run if it’s high volatility.",
        ],
      },
      {
        heading: "Volatility is the “personality” of the slot",
        paragraphs: [
          "Low volatility games tend to pay smaller wins more often. High volatility games pay less often, but when they hit, they can hit big.",
          "If you’re playing with a smaller budget, a lower volatility slot can feel more comfortable because you’ll likely have more frequent feedback.",
        ],
        tips: [
          "Small bankroll: aim for lower volatility and smaller bet sizes.",
          "Chasing big features: choose higher volatility, but accept longer dry spells.",
          "Always set a session limit before you spin—especially on high volatility titles.",
        ],
      },
      {
        heading: "A simple way to choose a slot for tonight",
        paragraphs: [
          "Ask yourself: do you want frequent small wins (entertainment), or are you fine with a slower session that’s hunting for a bigger moment?",
          "Then pick the game and bet size to match. Most frustration comes from choosing a high volatility game while expecting constant hits.",
        ],
      },
    ],
  },
  {
    id: "slots-feature-reading",
    category: "Slots",
    title: "How to read a slot: features that actually change your odds",
    readingTime: "9 min",
    excerpt:
      "Free spins, multipliers, buy‑features, bonus rounds… not all mechanics affect your session the same way. Here’s what matters when you’re deciding.",
    sections: [
      {
        heading: "Free spins and multipliers: the two most common value drivers",
        paragraphs: [
          "Free spins often shift a lot of the game’s value into the bonus round. That can mean longer stretches of quiet spins and a more “all or nothing” feeling.",
          "Multipliers (especially sticky or escalating multipliers) can turn average wins into memorable ones, but usually come with higher variance.",
        ],
      },
      {
        heading: "Feature buys: convenience, not a shortcut to profit",
        paragraphs: [
          "Buying a feature usually compresses time: you pay more upfront to access the bonus sooner. It doesn’t magically improve expected value.",
          "If feature buys exist, use them only when you understand the cost and you’re comfortable with the risk of a quick loss.",
        ],
      },
      {
        heading: "A quick checklist before you start",
        paragraphs: [
          "Check max win, volatility notes, and whether the best wins live in the bonus feature. Those three details tell you what the session will feel like.",
          "If a slot has lots of side bets or toggles, start with the default settings until you understand the paytable.",
        ],
      },
    ],
  },
  {
    id: "live-baccarat-beginner",
    category: "Live Casino",
    title: "Live Baccarat, explained like you’re at the table",
    readingTime: "11 min",
    excerpt:
      "Baccarat is popular because the core decision is simple. The tricky part is side bets, table pace, and managing your session. Let’s keep it practical.",
    sections: [
      {
        heading: "The simplest way to play",
        paragraphs: [
          "Most beginner sessions stick to two bets: Player or Banker. The hand closest to 9 wins. You don’t “choose cards”; you choose the side.",
          "The game moves quickly, so it helps to decide your approach before you click into a table.",
        ],
      },
      {
        heading: "Side bets: fun, but high variance",
        paragraphs: [
          "Side bets can be entertaining, but they usually carry higher house edge and bigger swings.",
          "If you use side bets, treat them as a small “spice” portion of your session, not the main plan.",
        ],
        tips: [
          "If you’re learning: skip side bets for the first 20–30 rounds.",
          "Set a time limit; live tables can pull you into long sessions.",
        ],
      },
      {
        heading: "Table selection and pace",
        paragraphs: [
          "Different tables run at different speeds. A slower table is often better for beginners because you can read the UI and understand results without pressure.",
          "If you feel rushed, switch tables. That’s normal, not a failure.",
        ],
      },
    ],
  },
  {
    id: "live-roulette-guide",
    category: "Live Casino",
    title: "Roulette: inside vs outside bets (a calm way to start)",
    readingTime: "10 min",
    excerpt:
      "Roulette looks chaotic because there are many bet types. The easiest entry is to start with outside bets, then explore inside bets when you’re comfortable.",
    sections: [
      {
        heading: "Outside bets: simple and steady",
        paragraphs: [
          "Outside bets (Red/Black, Odd/Even, 1–18/19–36) are the simplest. They pay smaller amounts but hit more often than single numbers.",
          "This makes them a good fit if you want longer play time and less volatility.",
        ],
      },
      {
        heading: "Inside bets: higher payout, bigger swings",
        paragraphs: [
          "Inside bets (single numbers, splits, streets) can pay more, but they hit less often. Your bankroll needs to handle longer gaps.",
          "If you enjoy the thrill, keep bet sizes small and treat it as entertainment, not certainty.",
        ],
        tips: [
          "Try 80% outside bets, 20% inside bets as a learning mix.",
          "If your session starts to feel stressful, move back to outside bets.",
        ],
      },
      {
        heading: "What not to chase",
        paragraphs: [
          "Streaks happen by chance. Don’t feel forced to “follow” or “fight” a streak. Use a plan you can stick with.",
          "The best roulette sessions are the ones where you can walk away calmly when you hit your limit.",
        ],
      },
    ],
  },
  {
    id: "sports-betting-basics",
    category: "Sports",
    title: "Sports betting basics: odds, lines, and what ‘value’ really means",
    readingTime: "12 min",
    excerpt:
      "A beginner-friendly guide to reading odds, understanding totals and handicaps, and making bets that fit your risk tolerance.",
    sections: [
      {
        heading: "Odds are probability with a price tag",
        paragraphs: [
          "Odds aren’t just “payout numbers”; they imply a probability. When you learn to read that implication, you stop making emotional bets.",
          "If you only pick teams you like, you’ll eventually run into odds that don’t match reality. That’s where disciplined betting matters.",
        ],
      },
      {
        heading: "Handicaps and totals (why they exist)",
        paragraphs: [
          "Handicaps balance mismatched teams by giving one side a head start. Totals (over/under) let you bet the game pace rather than the winner.",
          "These markets can be easier to approach than picking outright winners every time.",
        ],
      },
      {
        heading: "A practical risk rule",
        paragraphs: [
          "If you’re betting for entertainment, treat each bet as a small unit (for example, 1% to 3% of your total budget).",
          "It’s not about predicting perfectly; it’s about staying stable enough to enjoy the season.",
        ],
      },
    ],
  },
  {
    id: "bonuses-read-terms",
    category: "Bonuses",
    title: "Bonuses without regret: how to read terms before you click ‘claim’",
    readingTime: "9 min",
    excerpt:
      "Most bonus problems come from skipping two lines: game eligibility and turnover. Here’s a quick method to check terms in under a minute.",
    sections: [
      {
        heading: "The three lines that matter most",
        paragraphs: [
          "Before you claim anything, check three things: eligible games, turnover requirement, and withdrawal limits or maximum cashout rules.",
          "If any of these feel unclear, it’s better to ask support first than to discover restrictions after you’ve played.",
        ],
      },
      {
        heading: "Why some games are excluded",
        paragraphs: [
          "Some bonuses apply only to slot games and exclude live or fishing games. That’s normal: different game types have different risk profiles.",
          "If you like a particular category, choose promotions that match how you actually play.",
        ],
      },
      {
        heading: "A calm approach to promotions",
        paragraphs: [
          "Treat promotions as a ‘nice extra’, not the main reason you play. When you chase bonuses, you tend to break your own limits.",
          "If you claim a bonus, set a clear exit point: either you complete the turnover, or you stop when your time limit is reached.",
        ],
      },
    ],
  },
  {
    id: "monthly-competition-tips",
    category: "Bonuses",
    title: "Monthly deposit competition: how ranking usually works (and how to avoid surprises)",
    readingTime: "7 min",
    excerpt:
      "Ranking competitions can be fun, but you should know exactly what counts (and what doesn’t). Here’s a straightforward breakdown.",
    sections: [
      {
        heading: "What typically counts toward rank",
        paragraphs: [
          "Most deposit competitions count total deposit amount within the calendar window. It’s usually based on deposits, not net profit, and not total betting volume.",
          "The key is the time window. If the promo uses Syd/Melb time, make sure your deposits land in that period.",
        ],
      },
      {
        heading: "Your best habit: track deposits in one place",
        paragraphs: [
          "Keep a simple note of your deposit amounts and dates. If you ever need support to check a discrepancy, this makes it easier to resolve quickly.",
          "If you deposit via multiple methods, keep the references. It reduces back-and-forth.",
        ],
      },
    ],
  },
  {
    id: "payments-withdrawal-first",
    category: "Payments",
    title: "Deposit is easy. Withdrawal is the real test: a ‘withdrawal-first’ payment approach",
    readingTime: "8 min",
    excerpt:
      "The best payment method is the one that lets you withdraw smoothly. Here’s how to plan your payment choices so you don’t get stuck later.",
    sections: [
      {
        heading: "Stay consistent to keep your account history clean",
        paragraphs: [
          "Platforms review patterns. If your deposits and withdrawals look consistent (same name, same method, normal amounts), life is simpler.",
          "If you switch methods constantly, you may trigger extra verification steps. It’s not personal; it’s risk management.",
        ],
      },
      {
        heading: "A realistic recommendation",
        paragraphs: [
          "If you’re in Australia and you want to withdraw in AUD, bank rails are usually the least stressful path.",
          "Cards and wallets are fantastic for deposits. For withdrawals, be prepared that you may be asked to use a bank method instead.",
        ],
      },
    ],
  },
  {
    id: "mobile-play-tips",
    category: "Mobile",
    title: "Mobile play tips: how to make sessions smoother on iOS and Android",
    readingTime: "9 min",
    excerpt:
      "A practical checklist: network stability, battery settings, avoiding app interruptions, and the small UI tricks that save time during deposits and live games.",
    sections: [
      {
        heading: "Avoid the three classic mobile frustrations",
        paragraphs: [
          "First: unstable connection. Live games and fast slots feel awful on a weak network. If you can, use Wi‑Fi or a stable 5G signal.",
          "Second: background refresh and battery savers. They can pause your session or kill an authentication step.",
          "Third: too many apps open. If your phone is struggling, close heavy apps so the casino runs smoothly.",
        ],
      },
      {
        heading: "Small habits that help",
        paragraphs: [
          "Turn on auto‑rotate only when you need it; some games look better in landscape.",
          "If you’re using Apple Pay/Google Pay, confirm biometrics are working before you start a deposit.",
        ],
      },
    ],
  },
  {
    id: "safety-responsible",
    category: "Safety",
    title: "Safer play: limits, habits, and knowing when to stop (no lectures, just useful tools)",
    readingTime: "10 min",
    excerpt:
      "A non-judgmental guide to staying in control. It’s about reducing stress, not killing the fun.",
    sections: [
      {
        heading: "Two limits that actually work",
        paragraphs: [
          "Time limit: decide how long you’ll play before you start. When the timer ends, stop—even if you’re ‘almost’ at a feature.",
          "Spend limit: separate entertainment money from rent-and-bills money. If the session budget is gone, the session is done.",
        ],
        tips: [
          "Write your limits down. It sounds basic, but it works.",
          "If you’re tilted, take a 15‑minute break before making any new deposit.",
        ],
      },
      {
        heading: "When you should pause",
        paragraphs: [
          "If you’re chasing losses, increasing bet sizes quickly, or feeling angry, you’re not playing for fun anymore.",
          "Stopping early is a skill. The goal is to leave with your mood intact, not to win every session.",
        ],
      },
    ],
  },
  {
    id: "strategy-bankroll",
    category: "Strategy",
    title: "Bankroll habits that make casino sessions more enjoyable",
    readingTime: "9 min",
    excerpt:
      "No miracle systems here. Just a few simple habits that help you play longer and feel calmer, especially on high volatility games.",
    sections: [
      {
        heading: "Bet sizing is everything",
        paragraphs: [
          "A common mistake is betting too large relative to the session budget. It turns normal variance into panic.",
          "A calmer approach is to pick a bet size that gives you enough spins or rounds to actually enjoy the game.",
        ],
      },
      {
        heading: "A session plan you can follow",
        paragraphs: [
          "Pick a target number of spins or rounds, then pick a bet size that fits that plan.",
          "If you hit a big win, consider taking a portion off the table. It turns a lucky moment into a real result.",
        ],
      },
    ],
  },
  {
    id: "community-support",
    category: "Community",
    title: "Getting help fast: what to send support so issues get solved quicker",
    readingTime: "7 min",
    excerpt:
      "Support is fastest when you send the right details. Here’s the short checklist that avoids back-and-forth.",
    sections: [
      {
        heading: "The message that gets results",
        paragraphs: [
          "When you contact support about deposits, withdrawals, or bonus eligibility, include: your username, date/time, amount, method, and any reference/transaction ID.",
          "If it’s a game issue, add the game name, provider, and a screenshot if possible.",
        ],
        tips: [
          "Screenshots save time. One clear screenshot beats five messages.",
          "If you used crypto, include the transaction hash and the network name.",
        ],
      },
      {
        heading: "Keep it simple and specific",
        paragraphs: [
          "Avoid long stories. Support teams solve problems faster when the facts are clear and in one place.",
          "If you don’t know a reference number, say that upfront and share what you do have (time, amount, method).",
        ],
      },
    ],
  },
];

const categories: BlogCategory[] = [
  "Getting Started",
  "Slots",
  "Live Casino",
  "Sports",
  "Bonuses",
  "Payments",
  "Mobile",
  "Safety",
  "Strategy",
  "Community",
];

function Chip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
      style={{
        background: active ? "rgba(16,92,232,0.18)" : "rgba(255,255,255,0.04)",
        border: active ? "1px solid rgba(16,92,232,0.35)" : "1px solid rgba(255,255,255,0.10)",
        color: active ? "#93c5fd" : "rgba(226,232,240,0.9)",
      }}
    >
      {children}
    </button>
  );
}

function TipList({ tips }: { tips: string[] }) {
  return (
    <div className="mt-3 rounded-2xl p-4 border border-white/10 bg-white/[0.03]">
      <div className="text-slate-100 font-semibold text-xs mb-2 flex items-center gap-2">
        <span className="text-blue-300">★</span> Quick tips
      </div>
      <ul className="space-y-2 text-xs text-slate-300 leading-relaxed">
        {tips.map((t) => (
          <li key={t} className="flex gap-2">
            <span className="text-slate-500">•</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PostCard({
  post,
  open,
  onToggle,
}: {
  post: BlogPost;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <article className="glass-card rounded-2xl overflow-hidden border border-white/10">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 cursor-pointer"
        style={{ background: open ? "rgba(16,92,232,0.07)" : "transparent" }}
        aria-expanded={open}
      >
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
              style={{
                background: "rgba(224,201,127,0.12)",
                border: "1px solid rgba(224,201,127,0.25)",
                color: "#e0c97f",
              }}
            >
              {post.category}
            </span>
            <span className="text-slate-500 text-[11px]">{post.readingTime} read</span>
          </div>
          <h3 className="text-slate-100 font-bold text-sm sm:text-base">{post.title}</h3>
          <p className="text-slate-400 text-xs sm:text-sm mt-2 leading-relaxed">{post.excerpt}</p>
        </div>
        <span
          className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "#93c5fd",
          }}
        >
          <i className={open ? "ri-subtract-line" : "ri-add-line"}></i>
        </span>
      </button>

      {open && (
        <div className="px-6 pb-6">
          <div className="h-px w-full mb-5" style={{ background: "rgba(255,255,255,0.08)" }} />
          <div className="space-y-6">
            {post.sections.map((s) => (
              <section key={s.heading}>
                <h4 className="text-slate-100 font-semibold text-sm mb-2">{s.heading}</h4>
                <div className="space-y-3">
                  {s.paragraphs.map((p, idx) => (
                    <p key={idx} className="text-slate-300 text-sm leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
                {s.tips?.length ? <TipList tips={s.tips} /> : null}
              </section>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function Blog() {
  const [category, setCategory] = useState<BlogCategory | "All">("All");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(posts[0]?.id ?? null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      const categoryOk = category === "All" ? true : p.category === category;
      if (!categoryOk) return false;
      if (!q) return true;
      const hay = `${p.title} ${p.excerpt} ${p.category}`.toLowerCase();
      return hay.includes(q);
    });
  }, [category, query]);

  return (
    <section id="blog" className="py-12">
      <SectionTitle>Ace96au Blog</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 mb-8">
        <div className="glass-card rounded-2xl p-6">
          <h3 className="text-slate-100 font-bold text-base mb-2">Entertainment guides, explained like a human</h3>
          <p className="text-slate-300 text-sm leading-relaxed">
            This is a growing library of Ace96au topics: slots, live tables, sports markets, promotions, payments, mobile
            play, safer habits, and simple strategy. It’s written to be useful in real sessions, not as generic advice.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="text-slate-400 text-xs block mb-2">Search topics</label>
              <div
                className="rounded-2xl px-4 py-3 flex items-center gap-2"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}
              >
                <i className="ri-search-line text-slate-500"></i>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Try: slots, PayID, Baccarat…"
                  className="bg-transparent outline-none text-slate-200 text-sm w-full placeholder:text-slate-500"
                />
              </div>
            </div>

            <div className="sm:w-[200px]">
              <label className="text-slate-400 text-xs block mb-2">Filter</label>
              <div className="flex flex-wrap gap-2">
                <Chip active={category === "All"} onClick={() => setCategory("All")}>
                  All
                </Chip>
                <Chip active={category === "Getting Started"} onClick={() => setCategory("Getting Started")}>
                  Start
                </Chip>
                <Chip active={category === "Slots"} onClick={() => setCategory("Slots")}>
                  Slots
                </Chip>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <div className="text-slate-100 font-semibold text-sm">Browse by category</div>
              <div className="text-slate-400 text-xs">Pick one, then open any post</div>
            </div>
            <span className="text-slate-500 text-xs">{filtered.length} posts</span>
          </div>

          <div className="flex flex-wrap gap-2">
            <Chip active={category === "All"} onClick={() => setCategory("All")}>
              All
            </Chip>
            {categories.map((c) => (
              <Chip key={c} active={category === c} onClick={() => setCategory(c)}>
                {c}
              </Chip>
            ))}
          </div>

          <div className="mt-5 rounded-2xl p-4 border border-white/10 bg-white/[0.03]">
            <div className="text-slate-100 font-semibold text-xs mb-2">A quick note</div>
            <p className="text-slate-300 text-xs leading-relaxed">
              If you want a specific topic added (game provider deep dive, new promo breakdown, sports market explainer),
              tell us what you want to learn and we’ll expand this section.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {filtered.map((p) => (
          <PostCard
            key={p.id}
            post={p}
            open={openId === p.id}
            onToggle={() => setOpenId(openId === p.id ? null : p.id)}
          />
        ))}
      </div>
    </section>
  );
}

